<template>
  <div id="app" v-if="shouldShowApp">
    <ImpersonationBanner />
    <router-view />

    <!-- Global bulk upload status bar -->
    <BulkUploadStatusBar />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted } from "vue";
import { useFrontegg, useFronteggAuthGuard } from "@/cognito/frontegg-shim";
import { isPublicRoute } from "@/utils/routeUtils";
import { useCognitoAuth } from "@/cognito/cognito.config";
import { cognitoSignOut } from "@/cognito/auth";
import {
  clearCompanyCrossAppHandoffCookiesForLogout,
  syncSwitchableCompanyHandoffCookie,
} from "@/cognito/crossAppLogoutCookie";
import { useUserStore } from "@/stores/user";
import { restorePersistedCompanySessionIfNeeded } from "@/utils/companySessionRestore";
import { reactToPeerLogoutWithRestoredSession } from "@/utils/peerLogoutReact";
import BulkUploadStatusBar from "@/components/shared/bulk-upload/BulkUploadStatusBar.vue";
import ImpersonationBanner from "@/components/shared/ImpersonationBanner.vue";
import {
  SLASHEXPERTS_COMPANY_APP_SWITCH_CHANNEL,
  consumeAppSwitchBroadcastIfFromThisTab,
} from "@/utils/tabAppSwitchBroadcast";

export default defineComponent({
  name: "App",
  components: {
    BulkUploadStatusBar,
    ImpersonationBanner,
  },
  setup() {
    const isPublic = isPublicRoute();
    console.log("📱 App.vue: Route check result:", {
      isPublic,
      pathname: window.location.pathname,
    });

    if (isPublic) {
      // Public route: Frontegg was NOT initialized. Show app immediately.
      console.log("✅ App.vue: PUBLIC route - showing app immediately");
      return {
        shouldShowApp: true,
      };
    } else {
      // Private route: Frontegg was initialized. Use hooks normally.
      console.log("🔒 App.vue: PRIVATE route - using Frontegg hooks");
      const { fronteggLoaded, authState } = useFrontegg();
      useFronteggAuthGuard();

      // Cross-tab logout + cross-app switch: Cognito broadcasts (logout → sign-out;
      // app switch → full reload to app root so Pinia matches shared OIDC session).
      if (useCognitoAuth) {
        const userStore = useUserStore();
        let logoutChannel: BroadcastChannel | null = null;
        let appSwitchChannel: BroadcastChannel | null = null;
        const refreshSwitchableHandoffCookie = (): void => {
          restorePersistedCompanySessionIfNeeded(userStore);
          syncSwitchableCompanyHandoffCookie(
            userStore.email,
            Boolean(userStore.switchable)
          );
        };
        const onVisibilityForCrossAppLogout = (): void => {
          if (document.visibilityState !== "visible") return;
          reactToPeerLogoutWithRestoredSession();
          refreshSwitchableHandoffCookie();
        };
        const onWindowFocusForCrossAppLogout = (): void => {
          reactToPeerLogoutWithRestoredSession();
          refreshSwitchableHandoffCookie();
        };
        onMounted(() => {
          reactToPeerLogoutWithRestoredSession();
          refreshSwitchableHandoffCookie();
          document.addEventListener(
            "visibilitychange",
            onVisibilityForCrossAppLogout
          );
          window.addEventListener("focus", onWindowFocusForCrossAppLogout);
          try {
            logoutChannel = new BroadcastChannel("cognito_company_logout");
            logoutChannel.onmessage = (event: MessageEvent) => {
              if (event.data?.type === "logout") {
                // Clear local OIDC + Pinia state then redirect to Cognito /logout
                // so that HomeView on return finds no valid session and redirects to login.
                clearCompanyCrossAppHandoffCookiesForLogout();
                void cognitoSignOut();
              }
            };
          } catch {
            /* BroadcastChannel not available — cross-tab logout not supported */
          }
          try {
            appSwitchChannel = new BroadcastChannel(
              SLASHEXPERTS_COMPANY_APP_SWITCH_CHANNEL
            );
            appSwitchChannel.onmessage = (event: MessageEvent) => {
              if (event.data?.type !== "app_switch_reset") return;
              if (
                consumeAppSwitchBroadcastIfFromThisTab(
                  event.data?.sourceBroadcastId as string | undefined
                )
              ) {
                return;
              }
              const root = new URL(
                process.env.BASE_URL || "/",
                globalThis.location.origin
              ).href;
              globalThis.location.assign(root);
            };
          } catch {
            /* BroadcastChannel not available — cross-tab app switch not supported */
          }
        });
        onUnmounted(() => {
          document.removeEventListener(
            "visibilitychange",
            onVisibilityForCrossAppLogout
          );
          window.removeEventListener("focus", onWindowFocusForCrossAppLogout);
          try {
            logoutChannel?.close();
          } catch {
            /* ignore */
          }
          try {
            appSwitchChannel?.close();
          } catch {
            /* ignore */
          }
        });
      }

      console.log("🔒 App.vue: Frontegg loaded state:", fronteggLoaded);
      return {
        shouldShowApp: fronteggLoaded,
        authState,
      };
    }
  },
});
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

nav {
  padding: 30px;
}

nav a {
  font-weight: bold;
  color: #2c3e50;
}

nav a.router-link-exact-active {
  color: #42b983;
}
</style>
